import * as React from "react";
import { Typography, Box, Button, Grid, Dialog, DialogContent, useMediaQuery, IconButton, Icon, Stepper, Step, StepLabel, MenuItem } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@mui/styles";
import CloseModalIcon from "../../icons/RoundCloseIcon.svg";
import VisaLogo from "../../icons/visa-logo.png";
import MasterCardLogo from "../../icons/masterCardIcon.png";
import AddCardPaymentIcon from "../../icons/addCardIcon.png";
import CardCircuitIcon from "../../icons/CardCircuitIcon.svg";
import SpinnerModal from "./SpinnerModal";
import { getCreateBankCard, selectPaymentState, selectBankCardTypeDetails, getBankCardType, getPaymentMethods, clearBankAndCardStates } from "../../resui/payments/sagas/residentpaymentsSlice";
import { approvedISOStandards } from "../util/approvedISOStandards";
import { UIContextEnum, defaultUIContext } from "../../pmui/residents/MakeAdhocOtp";
import { getResidentBankCardType, selectResidentBankCardType, setCreateBankCardRequest } from "../../pmui/residents/saga/pmResidentsSlice";
import { NumberRegex } from "../../utils/regex";
import { usePmActor } from "../login/usePmActor";
import Amex from "../../icons/AMEX.svg";
import Discover from "../../icons/Discover.svg";
import RoundBackIcon from "../../icons/RoundBackIcon.svg";
import { useFormik } from "formik";
import { schemaValidationBankCardType, schemaValidationBankCard } from "../../utils/validation";
import InputControllerBank from "../../components/Form/InputControllerBank";
import CustomDropdown from "../../components/customDropdown";
import CheckBox from "../../icons/CheckBox.svg";
import UnCheckedBox from "../../icons/UnCheckedBox.svg";
import { selectAuthState } from "../../shared/sagas/auth/authSlice";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    margin: "1px",
    position: "relative",
  },
  stepperContainer: {
    paddingTop: "50px",
    width: "100%",
  },
  stepper: {
    width: "100%",
    "& .MuiStepLabel-root": {
      display: "flex",
      flexDirection: "column-reverse !important",
      marginTop: "-65px",
    },
    "& .MuiStepLabel-label": {
      display: "flex",
      justifyContent: "center",
    },
    "& .MuiStepLabel-labelContainer": {
      marginBottom: "9px",
    },
    "& .MuiStepConnector-root": {
      top: "-5px",
    },
    "& .MuiStepIcon-root": {
      color: "#F1F0F5",
      width: "29px",
      height: "29px",
    },
    "& .MuiStepIcon-root.Mui-completed": {
      color: "#39DB8D",
    },
    "& .MuiStepIcon-root .MuiStepIcon-text": {
      fill: "#14142B",
      fontWeight: "600",
    },
    "& .MuiStepIcon-root.Mui-active": {
      color: "#39DB8D",
    },
    "& .MuiStepIcon-root.Mui-active .MuiStepIcon-text": {
      fill: "#fff",
    },
  },
  stepperText: {
    fontFamily: "Work Sans !important",
    fontStyle: "normal",
    fontWeight: "400",
    fontSize: "18px !important",
    lineHeight: "21px !important",
    maxWidth: "140px",
    color: "#14142B",
  },
  modalText: {
    fontFamily: "Work Sans !important",
    fontStyle: "normal !important",
    fontWeight: "400 !important",
    fontSize: "14px !important",
    lineHeight: "22px !important",
    color: "#000000 !important",
    textAlign: "center",
  },
  cardPreviewText: {
    fontFamily: "Work Sans !important",
    fontStyle: "normal !important",
    fontWeight: "600 !important",
    fontSize: "16px !important",
    lineHeight: "22px !important",
    color: "#FFFFFF !important",
  },
  accpetText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: 400,
    fontSize: '16px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
    marginLeft: "10px !important",
    cursor: "pointer !important",
  },
}));

interface NewBankCardProps {
  openCardModal: boolean;
  handleCloseCardModal: () => void;
  handleCloseGoBackCardModal: () => void;
  smallVersion?: boolean;
  title?: string;
}

const steps = ["Card Details", "Confirm Details"];

export default function AddNewCardPaymentModel(props: NewBankCardProps) {
  const currentUI = React.useContext(defaultUIContext);
  const [isPmActor] = usePmActor();
  const classes = useStyles();
  const isOnMobileScreen = useMediaQuery("(max-width:600px)");
  const dispatch = useDispatch();
  const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;

  const [activeStep, setActiveStep] = React.useState(0);
  const [cardGlobalError, setCardGlobalError] = React.useState<string | null>(null);
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const { ipaUser } = useSelector(selectAuthState);

  const initialValues: schemaValidationBankCardType = {
    Title: props.title ?? "",
    CardNumber: "",
    SecurityCode: "",
    ExpirationDate: "",
    CardholderName: "",
    NickName: "",
    City: "",
    State: approvedStates[0].Code,
    Zip: "",
    Line1: "",
    Country: "US",
    CardNetwork: "",
    CardType: "",
  }
  const formik = useFormik<schemaValidationBankCardType>({
    initialValues: initialValues,
    validationSchema: schemaValidationBankCard,
    onSubmit: async (values) => {
      setActiveStep(1);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { isCardSuccess, isError, cardErrorMessage, isFetching } = useSelector(selectPaymentState);
  const bankCardTypebyBIN = useSelector(currentUI !== UIContextEnum.pmUI ? selectBankCardTypeDetails : selectResidentBankCardType);

  React.useEffect(() => {
    formik.setFieldValue("CardNetwork", bankCardTypebyBIN?.CardNetwork);
    formik.setFieldValue("CardType", bankCardTypebyBIN?.CardType);
  }, [bankCardTypebyBIN])

  React.useEffect(() => {
    if (formik.values.CardNumber.length >= 6) {
      dispatch({
        type: currentUI !== UIContextEnum.pmUI ? getBankCardType.type : getResidentBankCardType.type,
        payload: {
          cardPrefix: formik.values.CardNumber,
        },
      });
    }
  }, [dispatch, formik.values.CardNumber]);

  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
    !(isPmActor || currentUI === UIContextEnum.pmUI) && dispatch(getPaymentMethods());
  }, [dispatch, getPaymentMethods]);

  React.useEffect(() => {
    if (isCardSuccess) {
      setSpinnerStatus("stop");
    } else if (isError) {
      setIsSpinnerModalOpen(false);
      setCardGlobalError(cardErrorMessage);
      dispatch({ type: clearBankAndCardStates.type });
    }
  }, [isCardSuccess, isError, cardErrorMessage]);

  const [isFutureUse, setIsFutureUse] = React.useState(false);
  const isAdhocUI = isPmActor || currentUI === UIContextEnum.pmUI;

  const handleAddCardSubmit = () => {
    const names = formik.values.CardholderName.split(" ");

    setCardGlobalError(null);
    dispatch({ type: clearBankAndCardStates.type });

    if (Boolean(formik.isValid)) {
      !isAdhocUI && setIsSpinnerModalOpen(true);
      dispatch({
        type: isAdhocUI ? setCreateBankCardRequest.type : getCreateBankCard.type,
        payload: {
          CardType: isAdhocUI ? bankCardTypebyBIN?.CardType : undefined,
          ProcessAsCardType: isAdhocUI ? bankCardTypebyBIN?.ProcessAsCardType : undefined,
          CardNetwork: isAdhocUI ? bankCardTypebyBIN?.CardNetwork : undefined,
          CardNumber: formik.values.CardNumber,
          SecurityCode: formik.values.SecurityCode,
          BillingAddress: {
            City: formik.values.City,
            State: formik.values.State,
            Zip: formik.values.Zip,
            Line1: formik.values.Line1,
            Country: formik.values.Country,
          },
          ExpirationDate: formik.values.ExpirationDate.split("/").join(""),
          CardholderFirstname: names[0],
          CardholderLastname: names[1],
          NickName: formik.values.NickName ?? null,
          IsNotAdhoc: isFutureUse,
        },
      });

      // skip spinner for adhoc otp and close modal
      isAdhocUI && props.handleCloseCardModal();
    }
  };

  const setCardExpirationHandler = (expiration: any) => {
    formik.setFieldValue("ExpirationDate", expiration)
    if (expiration.length === 2 && !expiration.includes("/")) {
      formik.setFieldValue("ExpirationDate", [expiration.slice(0, 2), "/", expiration.slice(2)].join(""));
    }
  };

  const handleGoBack = () => {
    setCardGlobalError(null);
    if (activeStep === 0) {
      props.handleCloseGoBackCardModal();
    } else if (activeStep === 1) {
      setActiveStep(0);
    }
  }

  const [selectedState, setSelectedState] = React.useState(approvedStates[0].Code);
  const resetStateSelection = () => {
    formik.setFieldValue("State", "");
    setSelectedState("");
  };

  React.useEffect(() => {
    formik.values.Country && ["US", "CA"].includes(formik.values.Country) && approvedStates.map((stateRecord) => stateRecord.Code).includes(formik.values.State) && setSelectedState(formik.values.State);
  }, [formik]);

  const customDropdownStyle = {
    paddingLeft: "5px",
    borderRadius: "24px",
    height: "49px",
    backgroundColor: "white",
    width: '100%',
    fontFamily: "Work Sans",
  }

  return (
    <div>
      <Dialog
        open={props.openCardModal}
        onClose={props.handleCloseCardModal}
        fullScreen={isOnMobileScreen}
        fullWidth={true}
        disableEnforceFocus
        PaperProps={{
          style: {
            pointerEvents: "auto",
            maxWidth: isOnMobileScreen ? "100%" : props.smallVersion ? "472px" : "600px",
            minHeight: isOnMobileScreen ? "100%" : props.smallVersion ? "523px" : "700px",
            borderRadius: isOnMobileScreen ? "none" :"24px",
            padding: props.smallVersion ? "0px" : "0px 10px 20px",
          },
        }}
      >
        <DialogContent sx={{ padding: props.smallVersion ? "20px 24px" : "40px 30px" }}>
          <Box sx={{ position: "relative" }}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', minHeight: 50 }}>
              <Box sx={{ position: 'absolute', left: 0, top: 0 }}>
                <IconButton sx={{ ml: "-8px"  }} onClick={handleGoBack}>
                  <Icon sx={{ width: 26, height: 26 }}>
                    <img src={RoundBackIcon} alt="Back" width={26} height={26} />
                  </Icon>
                </IconButton>
              </Box>
              <Box className={classes.stepperContainer} sx={{ display: isOnMobileScreen ? "none" : "block", mx: 4 }}>
                <Stepper alternativeLabel activeStep={activeStep} classes={{ root: classes.stepper }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel sx={{ display: "flex", justifyContent: "center" }}>
                        <Typography className={classes.stepperText}>{label}</Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
              <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
                <IconButton sx={{ mr: "-8px"  }} onClick={() => props.handleCloseCardModal()}>
                  <Icon sx={{ width: 26, height: 26 }}>
                    <img src={CloseModalIcon} alt="Close" width={26} height={26} />
                  </Icon>
                </IconButton>
              </Box>
            </Box>

            {activeStep === 0 ? (
              <Box component="form" onSubmit={formik.handleSubmit} noValidate>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"NickName"} 
                      label={"Set an Optional Nick Name"} 
                      value={formik.values.NickName ?? ""}
                      errors={formik.errors?.NickName}
                      touched={formik.touched?.NickName}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue("NickName", e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"CardNumber"} 
                      label={"Card Number"} 
                      value={formik.values.CardNumber ?? ""}
                      errors={formik.errors?.CardNumber}
                      touched={formik.touched?.CardNumber}
                      onBlur={formik.handleBlur}
                      onChange={(e) => {
                        if (e.target.value === "" || NumberRegex.test(e.target.value)) {
                          formik.setFieldValue("CardNumber", e.target.value);
                        }
                      }}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"SecurityCode"} 
                      label={"CVC"} 
                      value={formik.values.SecurityCode ?? ""}
                      errors={formik.errors?.SecurityCode}
                      touched={formik.touched?.SecurityCode}
                      onBlur={formik.handleBlur}
                      onChange={(e) => {
                        if (e.target.value === "" || NumberRegex.test(e.target.value)) {
                          formik.setFieldValue("SecurityCode", e.target.value);
                        }
                      }}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"ExpirationDate"} 
                      label={"Expiration Date"}
                      placeholder={"MM/YYYY"}
                      value={formik.values.ExpirationDate ?? ""}
                      errors={formik.errors?.ExpirationDate}
                      touched={formik.touched?.ExpirationDate}
                      onBlur={formik.handleBlur}
                      onChange={(e) => setCardExpirationHandler(e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"CardholderName"} 
                      label={"Name as it appears on Card"} 
                      value={formik.values.CardholderName ?? ""}
                      errors={formik.errors?.CardholderName}
                      touched={formik.touched?.CardholderName}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue("CardholderName", e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"Line1"} 
                      label={"Billing Address"} 
                      value={formik.values.Line1 ?? ""}
                      errors={formik.errors?.Line1}
                      touched={formik.touched?.Line1}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue("Line1", e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"City"} 
                      label={"City"} 
                      value={formik.values.City ?? ""}
                      errors={formik.errors?.City}
                      touched={formik.touched?.City}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue("City", e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    {formik.values.Country && ["US", "CA"].includes(formik.values.Country) ? (
                      <CustomDropdown
                        id={"state-dropdown"}
                        isFullWidth
                        label={"State"}
                        labelId={""}
                        value={selectedState}
                        handleChange={(e) => {
                          setSelectedState(e.target?.value);
                          formik.setFieldValue("State", e.target?.value);
                        }}
                        customStyle={customDropdownStyle}
                      >
                        {formik.values.Country === "US" ? 
                          approvedStates.map((stateRecord) => (
                            <MenuItem style={{ height: "36px" }} key={stateRecord.Code} value={stateRecord.Code}>
                              {stateRecord.State + " - " + stateRecord.Code}
                            </MenuItem>
                          )) : approvedProvinces.map((provinceRecord) => (
                            <MenuItem style={{ height: "36px" }} key={provinceRecord.Code} value={provinceRecord.Code}>
                              {provinceRecord.Province + " - " + provinceRecord.Code}
                            </MenuItem>
                          ))
                        }{" "}
                      </CustomDropdown>
                    ) : (
                      <InputControllerBank 
                        name={"State"} 
                        label={"State"} 
                        value={formik.values.State ?? ""}
                        errors={formik.errors?.State}
                        touched={formik.touched?.State}
                        onBlur={formik.handleBlur}
                        onChange={(e) => formik.setFieldValue("State", e.target.value)}
                        isfullWidth={true} 
                      />
                    )}
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"Zip"} 
                      label={"Zip"} 
                      value={formik.values.Zip ?? ""}
                      errors={formik.errors?.Zip}
                      touched={formik.touched?.Zip}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue("Zip", e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <CustomDropdown
                      label={"Country"}
                      isFullWidth
                      handleChange={(e: any) => {
                        resetStateSelection();
                        // update selected country
                        formik.setFieldValue("Country", e.target?.value);
                      }}
                      value={formik.values.Country || ""}
                      customStyle={customDropdownStyle}
                    >
                      {approvedCountries.map((countryRecord) => (
                        <MenuItem style={{ height: "36px" }} key={countryRecord.Code} value={countryRecord.Code}>
                          {countryRecord.Country + " - " + countryRecord.Code}
                        </MenuItem>
                      ))}
                    </CustomDropdown>
                  </Grid>
                </Grid>

                <div style={{ height: 42, width: "100%" }}></div>

                <Box sx={{ position: "absolute", bottom: 0, right: 0 }}>
                  <Button
                    disabled={Boolean(!formik.isValid)}
                    style={{
                      width: "99px",
                      fontFamily: "'Work Sans', sans-serif",
                      borderRadius: "24px",
                      backgroundColor: Boolean(!formik.isValid) ? "#D3D3D3" : "#39A2DB",
                      height: "33px",
                      textTransform: "capitalize",
                      fontSize: "18px",
                      color: "#fff",
                    }}
                    size="small"
                    type="submit"
                    id="continueNewBankBtn2"
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                {cardGlobalError?.length ? (
                  <Typography
                    style={{
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: "normal",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "18px",
                      color: "#DB3939",
                      textAlign: "center",
                      borderTop: "2px  solid #DB3939",
                      borderRadius: "24px",
                      padding: "12px 20px",
                      margin: "20px auto 0px",
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {cardGlobalError}
                  </Typography>
                ) : (
                  ""
                )}

                <Box sx={{ width: 319, borderRadius: "24px", background: "#84C7EB", padding: "11px 16px 17px", marginTop: "10px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <Typography className={classes.cardPreviewText}>
                        {bankCardTypebyBIN?.CardType === "DB" ? "Debit Card" : bankCardTypebyBIN?.CardType === "CC" ? "Credit Card" : "Card"}
                      </Typography>
                    </div>
                    <div>
                      {bankCardTypebyBIN?.CardNetwork?.toLowerCase() === "mastercard" ? (
                        <img src={MasterCardLogo} alt="Master" style={{ width: "15px", height: "12px" }} />
                      ) : bankCardTypebyBIN?.CardNetwork?.toLowerCase() === "visa" ? (
                        <img src={VisaLogo} alt="card" style={{ width: "29px", height: "9px" }} />
                      ) : ["amex","american express"].includes(bankCardTypebyBIN?.CardNetwork?.toLowerCase())? (
                        <img src={Amex} alt="card" style={{ width: "29px", height: "9px" }} />
                      ) : bankCardTypebyBIN?.CardNetwork?.toLowerCase() === "discover" ? (
                        <img src={Discover} alt="card" style={{ width: "29px", height: "9px" }} />
                      ) : (
                        <img src={AddCardPaymentIcon} alt="card" style={{ width: "15px", height: "12px", marginBottom: '10px' }} />
                      )}
                    </div>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "10px" }}>
                    <div>
                      <Typography className={classes.cardPreviewText}>
                        {formik.values.NickName || ""}
                      </Typography>
                    </div>
                    <div>
                      <Typography className={classes.cardPreviewText}>
                        {formik.values.SecurityCode}
                      </Typography>
                    </div>
                  </Box>
                  <Box sx={{ marginTop: "12px" }}>
                    <img src={CardCircuitIcon} alt="" />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "10px" }}>
                    <div>
                      <Typography className={classes.cardPreviewText}>
                        {formik.values.CardNumber}
                      </Typography>
                    </div>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "10px" }}>
                    <div>
                      <Typography className={classes.cardPreviewText}>
                        {formik.values.CardholderName}
                      </Typography>
                    </div>
                    <div>
                      <Typography className={classes.cardPreviewText}>
                        {formik.values.ExpirationDate}
                      </Typography>
                    </div>
                  </Box>
                </Box>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"Line1"} 
                      label={"Address"} 
                      value={formik.values.Line1 ?? ""}
                      errors={formik.errors?.Line1}
                      touched={formik.touched?.Line1}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue("Line1", e.target.value)}
                      isfullWidth={true} 
                      readonly={true}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"City"} 
                      label={"City"} 
                      value={formik.values.City ?? ""}
                      errors={formik.errors?.City}
                      touched={formik.touched?.City}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue("City", e.target.value)}
                      isfullWidth={true} 
                      readonly={true}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"State"} 
                      label={"State"} 
                      value={formik.values.State ?? ""}
                      errors={formik.errors?.State}
                      touched={formik.touched?.State}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue("State", e.target.value)}
                      isfullWidth={true} 
                      readonly={true}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"Zip"} 
                      label={"Zip"} 
                      value={formik.values.Zip ?? ""}
                      errors={formik.errors?.Zip}
                      touched={formik.touched?.Zip}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue("Zip", e.target.value)}
                      isfullWidth={true} 
                      readonly={true}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"Country"} 
                      label={"Country"} 
                      value={approvedCountries.find((ac) => ac.Code === formik.values.Country)?.Country ?? ''}
                      errors={formik.errors?.Country}
                      touched={formik.touched?.Country}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue("Country", e.target.value)}
                      isfullWidth={true} 
                      readonly={true}
                    />
                  </Grid>
                </Grid>

                <Typography className={classes.modalText}>
                  Please review the information above before proceeding.
                </Typography>

                {ipaUser?.currentProfile.isActing && (
                  <Box sx={{ display: 'flex', alignItems: 'center', padding: "20px 20px 0", width: "100%" }}>
                    <img src={isFutureUse ? CheckBox : UnCheckedBox} style={{ width: "11px", height: "11px", cursor: "pointer" }} alt='Checkmark' onClick={() => setIsFutureUse(!isFutureUse)} />
                    <Typography className={classes.accpetText} variant="caption" onClick={() => setIsFutureUse(!isFutureUse)}>
                      Add to tenant wallet
                    </Typography>
                  </Box>                  
                )}

                <div style={{ height: 42, width: "100%" }}></div>

                <Box sx={{ position: "absolute", bottom: 0, right: 0 }}>
                  <Button
                    disabled={Boolean(!formik.isValid || isFetching)}
                    style={{
                      width: "99px",
                      fontFamily: "'Work Sans', sans-serif",
                      borderRadius: "24px",
                      backgroundColor: Boolean(!formik.isValid) ? "#D3D3D3" : "#39A2DB",
                      height: "33px",
                      textTransform: "capitalize",
                      fontSize: "18px",
                      color: "#fff",
                    }}
                    size="small"
                    onClick={handleAddCardSubmit}
                    className={isFetching ? '' : 'active'}//it's for e2e test
                    id="confirmNewBankCardBtn"
                  >
                    Confirm
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      {isSpinnerModalOpen ? <SpinnerModal isInvisible={true} width={props.smallVersion ? "472px" : "600px"} height={isOnMobileScreen ? "100%" : props.smallVersion ? "523px" : "700px"} openSpinnerModal={isSpinnerModalOpen} handleCloseSpinnerModal={handleCloseSpinnerModal} spinnerStatus={spinnerStatus} closeCancel={props.handleCloseCardModal} /> : <></>}
    </div>
  );
}
